import React, { useMemo, useContext, useCallback } from 'react';
import { ThemeContext } from 'styled-components';

import { useDispatch } from 'react-redux';
import closeModalByName from 'ia-react-core/~/actions/modal-fn/closeModalByName';
import { WithTranslation } from 'react-i18next';
import { Response } from 'ia-react-core/~/interfaces/Response';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import ErrorModal, { ErrorModalProps } from '../ErrorModal';
import ConfirmBar from '../../buttons/ConfirmBar';

import TimeFormat from '../../format/TimeFormat';

export interface DataErrorModalProps<E = {}> extends Omit<ErrorModalProps, 'error'> {
  error?: Response<E>;
  /** If the request for data is pending */
  pending?: boolean;
  /** Number of attempts of the current request. */
  attempts?: number;
  /** Maximum number of attempts for server errors. */
  maxAttempts?: number;
  /** Text for network errors */
  networkErrorMessage?: string;
  /** Text for bad requests */
  badRequestMessage?: string | ((error: Partial<Response<E>>) => string);
  /** Text for service unavailable */
  serviceUnavailableMessage?: string | ((error: Partial<Response<E>>) => string);
  /** Text for too many requests error service */
  tooManyRequestsMessage?: string | ((error: Partial<Response<E>>) => string);
  /** Text for server errors */
  serverErrorMessage?: string;
  /** Text for the "try again" message */
  tryAgainMessage?: string;
  /** Text of the cancel button */
  cancelButton?: string;
  /** Text of the retry button */
  retryButton?: string;
  /** Callback of the *onCancel* button. */
  onCancel?: () => void;
  /** Callback of the *Retry* button. Required. */
  onRetry: () => void;
}

interface DataErrorModalInternalProps extends WithTranslation {}

interface DataErrorModaComposedProps<E = {}> extends DataErrorModalProps<E>, DataErrorModalInternalProps {}

export interface DataErrorModaStatics {
  publicProps: StringKeyOf<DataErrorModalProps>[];
}

const DataErrorModalDefaultProps = {
  maxAttempts: 3,
};

/**
 * @since 0.21.0
 */
const DataErrorModal = <E = {}>({
  name,
  t, tReady, error, pending, networkErrorMessage, badRequestMessage, serverErrorMessage, tryAgainMessage, cancelButton, retryButton, attempts, maxAttempts, onCancel, onRetry, serviceUnavailableMessage, tooManyRequestsMessage,
  ...props
}: DataErrorModaComposedProps<E>) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();

  const hasReachedMaxAttempts = useMemo(() => (
    error && error.status >= 500 && attempts > maxAttempts
  ), [attempts, error, maxAttempts]);

  const errorMessage = useMemo(() => {
    if (error && error.status === 0) {
      return networkErrorMessage || t('networkError');
    }
    if (error && error.status === 429) {
      if (typeof tooManyRequestsMessage === 'function') return tooManyRequestsMessage(error);
      const retryAfter = Date.parse(error.headers['retry-after']);
      return tooManyRequestsMessage ||
      (Number.isNaN(retryAfter) ?
        t('tooManyRequestError') :
        <>
          {t('tooManyRequestErrorWithTime')} <TimeFormat time={new Date(retryAfter)} isDateWithTime isBreaking={false} />
        </>
      );
    }
    if (error && error.status >= 400 && error.status < 500) {
      if (typeof badRequestMessage === 'function') return badRequestMessage(error);
      return badRequestMessage || t('badRequest');
    }

    if (error && error.status === 503) {
      if (typeof serviceUnavailableMessage === 'function') return serviceUnavailableMessage(error);
      const retryAfter = Date.parse(error.headers['retry-after']);
      return serviceUnavailableMessage ||
      (Number.isNaN(retryAfter) ?
        t('serviceUnavailable') :
        <>
          {t('serviceUnavailableWithTime')} <TimeFormat time={new Date(retryAfter)} isDateWithTime isBreaking={false} />
        </>
      );
    }

    return serverErrorMessage || t('serverError');
  }, [badRequestMessage, error, networkErrorMessage, serverErrorMessage, t, serviceUnavailableMessage, tooManyRequestsMessage]);

  const isRetryable = useMemo(() => (
    error && (
      (error.status === 0) ||
      (error.status >= 500 && !hasReachedMaxAttempts)
    )
  ), [error, hasReachedMaxAttempts]);

  const resolvedTryAgainMessage = useMemo(() => {
    if (!isRetryable) {
      return '';
    }

    return tryAgainMessage || t('tryAgain');
  }, [isRetryable, t, tryAgainMessage]);

  const modalTitle = useMemo(() => {
    if (error && error.status === 0) {
      return t('connectionErrorTitle');
    }

    if (error && (error.status === 503 || error.status === 429)) {
      return t('sorry');
    }

    return t('technicalErrorTitle');
  }, [error, t]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      dispatch(closeModalByName(name));
    }
  }, [dispatch, name, onCancel]);

  return (
    <ErrorModal
      name={name}
      modalTitle={modalTitle}
      isClosable={theme?.Modal.closableErrorModal}
      {...props}
    >
      <ErrorModal.Content>
        {errorMessage}{resolvedTryAgainMessage}
      </ErrorModal.Content>
      <ErrorModal.Footer>
        <ConfirmBar
          cancelDisabled={pending}
          onCancel={handleCancel}
          cancelButton={cancelButton || (t(isRetryable ? 'cancelButton' : 'okButton'))}
          confirmHidden={!isRetryable}
          confirmBusy={pending}
          onConfirm={onRetry}
          confirmButton={retryButton || t('retryButton')}
          align="center"
        />
      </ErrorModal.Footer>
    </ErrorModal>
  );
};

DataErrorModal.defaultProps = DataErrorModalDefaultProps;
DataErrorModal.publicProps = ['pending', 'attempts', 'maxAttempts', 'onCancel', 'onRetry', 'networkErrorMessage', 'badRequestMessage', 'serverErrorMessage', 'tryAgainMessage', 'cancelButton', 'retryButton', ...ErrorModal.publicProps];

export default DataErrorModal;
